import React, { useCallback, useState } from 'react';
import axios from 'axios';
import { ratingService } from '../../api/services/RatingService';
import type { SellerRatingViewModel } from '../../api/types/rating';

export interface RatingModalProps {
  soldListingId: string;
  listingTitle: string;
  onSubmitted: (rating: SellerRatingViewModel) => void;
  onClose: () => void;
}

const MAX_COMMENT_LENGTH = 2000;

/**
 * Buyer-facing rating dialog shown after purchase verification.
 * See specs/008-messaging-context-ratings contracts/rating.http.md §1.
 */
const RatingModal: React.FC<RatingModalProps> = ({
  soldListingId,
  listingTitle,
  onSubmitted,
  onClose,
}) => {
  const [stars, setStars] = useState<number>(0);
  const [hoveredStars, setHoveredStars] = useState<number | null>(null);
  const [comment, setComment] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const activeStars = hoveredStars ?? stars;
  const commentLength = comment.length;
  const overLimit = commentLength > MAX_COMMENT_LENGTH;
  const canSubmit = stars >= 1 && stars <= 5 && !overLimit && !submitting;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) {
      setErrorMessage('Lütfen 1 ile 5 arasında bir yıldız seçin.');
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);
    try {
      const trimmed = comment.trim();
      const result = await ratingService.submit({
        soldListingId,
        stars,
        comment: trimmed.length > 0 ? trimmed : null,
      });
      if (!result.isSuccess || !result.data) {
        setErrorMessage(result.message ?? 'Değerlendirme kaydedilemedi.');
        return;
      }
      onSubmitted(result.data);
      onClose();
    } catch (err) {
      const message = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message ?? err.message
        : 'Beklenmedik bir hata oluştu.';
      setErrorMessage(message);
    } finally {
      setSubmitting(false);
    }
  }, [canSubmit, comment, onClose, onSubmitted, soldListingId, stars]);

  return (
    <>
      <div
        className="modal fade show"
        style={{ display: 'block' }}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Satıcıyı Değerlendir</h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Kapat"
                onClick={() => !submitting && onClose()}
              />
            </div>
            <div className="modal-body">
              <p className="text-muted small mb-3">
                <strong>{listingTitle}</strong> ilanı için deneyiminizi paylaşın.
              </p>

              <div
                className="mb-3"
                role="radiogroup"
                aria-label="Yıldız seçin"
                onMouseLeave={() => setHoveredStars(null)}
              >
                {[1, 2, 3, 4, 5].map((value) => {
                  const filled = value <= activeStars;
                  return (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={stars === value}
                      className="btn btn-link p-0 me-2"
                      style={{ fontSize: '1.75rem', lineHeight: 1 }}
                      onMouseEnter={() => setHoveredStars(value)}
                      onFocus={() => setHoveredStars(value)}
                      onClick={() => setStars(value)}
                    >
                      <i
                        className={`${filled ? 'fas' : 'far'} fa-star`}
                        style={{ color: filled ? '#f5a524' : '#adb5bd' }}
                      />
                    </button>
                  );
                })}
                <span className="ms-2 text-muted small align-middle">
                  {stars > 0 ? `${stars} / 5` : 'Yıldız seçilmedi'}
                </span>
              </div>

              <div className="mb-2">
                <label htmlFor="rating-comment" className="form-label small mb-1">
                  Yorum (opsiyonel)
                </label>
                <textarea
                  id="rating-comment"
                  className={`form-control ${overLimit ? 'is-invalid' : ''}`}
                  rows={4}
                  maxLength={MAX_COMMENT_LENGTH + 1 /* allow paste, validate softly */}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Satıcı ile ilgili deneyiminizi yazın…"
                  disabled={submitting}
                />
                <div className="d-flex justify-content-between small mt-1">
                  <span className={overLimit ? 'text-danger' : 'text-muted'}>
                    {commentLength}/{MAX_COMMENT_LENGTH}
                  </span>
                  {overLimit && (
                    <span className="text-danger">Karakter sınırı aşıldı.</span>
                  )}
                </div>
              </div>

              {errorMessage && (
                <div className="alert alert-danger py-2" role="alert">
                  {errorMessage}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
                disabled={submitting}
              >
                Vazgeç
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={!canSubmit}
              >
                {submitting ? 'Gönderiliyor…' : 'Gönder'}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" />
    </>
  );
};

export default RatingModal;
